import React, { useContext, useEffect, useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Add, Edit, LocalHospital, LocationOn } from '@mui/icons-material'
import API from '../../../../../services/API'
import EmployeeSelector from '../../../../events/EmployeeSelector'
import IncidentLocationPicker from '../IncidentLocationPicker'
import { ControlRoomContext } from '../ControlPage'

const statusStyles = {
  Open: { bg: '#DCFCE7', color: '#166534' },
  Standby: { bg: '#FEF9C3', color: '#854D0E' },
  Closed: { bg: '#E2E8F0', color: '#334155' },
}

const emptyForm = {
  name: '',
  location: '',
  what3words: '',
  status: 'Open',
  assignedStaff: [],
}

export const TreatmentCentreForm = ({
  open,
  onClose,
  controlRoomID,
  centre,
  onSaved,
}) => {
  const [form, setForm] = useState(emptyForm)
  const [selectedStaff, setSelectedStaff] = useState(null)
  const [selectedRole, setSelectedRole] = useState('Clinician')
  const [saving, setSaving] = useState(false)
  // Map position, stored like an incident's: "lng,lat" string + 0-1 event
  // map fractions. The picker keeps both in sync whichever view is clicked.
  const [coordinates, setCoordinates] = useState('')
  const [eventMapCoordinates, setEventMapCoordinates] = useState(null)
  // Empty {} when the form is mounted outside a control room (the standalone
  // treatment locations page) — the picker then just shows the plain map.
  const { eventMap, mapView } = useContext(ControlRoomContext)

  useEffect(() => {
    setForm(
      centre
        ? {
            name: centre.name ?? '',
            location: centre.location ?? '',
            what3words: centre.what3words ?? '',
            status: centre.status ?? 'Open',
            assignedStaff: centre.assignedStaff ?? [],
          }
        : emptyForm,
    )
    setCoordinates(centre?.coordinates ?? '')
    setEventMapCoordinates(centre?.eventMapCoordinates ?? null)
    setSelectedStaff(null)
    setSelectedRole('Clinician')
  }, [centre, open])

  const staffUserId = (staff) =>
    staff?.user?._id || staff?.user || staff?.userAccount?._id

  const staffName = (staff) => {
    const user = staff?.user || staff?.userAccount
    return `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()
  }

  const addAssignedStaff = () => {
    if (!selectedStaff?.userAccount?._id) return

    setForm((prev) => {
      const nextStaff = [
        ...(prev.assignedStaff || []).filter(
          (staff) => staffUserId(staff) !== selectedStaff.userAccount._id,
        ),
        {
          user: selectedStaff.userAccount,
          role: selectedRole,
        },
      ]

      return {
        ...prev,
        assignedStaff: nextStaff,
      }
    })

    setSelectedStaff(null)
    setSelectedRole('Clinician')
  }

  const removeAssignedStaff = (userId) => {
    setForm((prev) => ({
      ...prev,
      assignedStaff: (prev.assignedStaff || []).filter(
        (staff) => staffUserId(staff) !== userId,
      ),
    }))
  }

  const handleSave = async () => {
    if (!form.name || saving) return

    setSaving(true)

    try {
      const payload = {
        ...form,
        coordinates,
        eventMapCoordinates,
        assignedStaff: (form.assignedStaff || []).map((staff) => ({
          user: staffUserId(staff),
          role: staff.role || 'Clinician',
        })),
        controlRoom: controlRoomID,
      }

      const res = centre?._id
        ? await API.post(
            `/commandcontrol/treatment-centres/${centre._id}`,
            payload,
          )
        : await API.post('/commandcontrol/treatment-centres', payload)

      if (res.status === 200) {
        onSaved(res.data)
        onClose()
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {centre ? 'Edit Treatment Centre' : 'New Treatment Centre'}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Centre name"
            value={form.name}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, name: e.target.value }))
            }
            fullWidth
            required
          />

          <TextField
            label="Location"
            value={form.location}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, location: e.target.value }))
            }
            fullWidth
          />

          <TextField
            label="What3Words"
            value={form.what3words}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, what3words: e.target.value }))
            }
            fullWidth
          />

          <Stack spacing={1}>
            <Typography fontWeight={900}>Map Position</Typography>
            <Typography variant="body2" color="text.secondary">
              Click the map to place this centre — it will show on the
              control map for dispatchers and crews.
            </Typography>
            <IncidentLocationPicker
              coordinates={coordinates}
              setCoordinates={setCoordinates}
              eventMapCoordinates={eventMapCoordinates}
              setEventMapCoordinates={setEventMapCoordinates}
              eventMap={eventMap}
              initialMapView={mapView || 'map'}
              compact
              height={300}
            />
          </Stack>

          <TextField
            label="Status"
            select
            value={form.status}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, status: e.target.value }))
            }
            fullWidth
          >
            {['Open', 'Standby', 'Closed'].map((x) => (
              <MenuItem key={x} value={x}>
                {x}
              </MenuItem>
            ))}
          </TextField>

          <Stack spacing={1.5}>
            <Typography fontWeight={900}>Assigned Staff</Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <EmployeeSelector
                value={selectedStaff}
                onChange={(e, value) => setSelectedStaff(value)}
                valueType="employee"
                label="Staff member"
                fullWidth
              />

              <TextField
                label="Role"
                select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                sx={{ minWidth: 150 }}
              >
                {['Clinician', 'Manager'].map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </TextField>

              <Button
                variant="outlined"
                onClick={addAssignedStaff}
                disabled={!selectedStaff}
                sx={{ minWidth: 90 }}
              >
                Add
              </Button>
            </Stack>

            {(form.assignedStaff || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No staff assigned yet.
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {form.assignedStaff.map((staff) => (
                  <Chip
                    key={staffUserId(staff)}
                    avatar={<Avatar src={staff.user?.photograph} />}
                    label={`${staffName(staff) || 'Staff member'} - ${
                      staff.role || 'Clinician'
                    }`}
                    onDelete={() => removeAssignedStaff(staffUserId(staff))}
                    sx={{ borderRadius: 999, fontWeight: 800 }}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const TreatmentCentreCard = ({ centre, onEdit }) => {
  const style = statusStyles[centre.status] || statusStyles.Standby
  const activePatients = centre.activePatients || []
  const activePatientCount = centre.stats?.totalPatients ?? activePatients.length

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 4,
        border: '1px solid rgba(15,23,42,0.08)',
        boxShadow: '0 10px 30px rgba(15,23,42,0.08)',
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#E6F7F9',
                color: '#1E7F88',
              }}
            >
              <LocalHospital />
            </Box>

            <Box>
              <Typography fontWeight={900}>{centre.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                Treatment Centre
              </Typography>
            </Box>
          </Stack>

          <IconButton
            onClick={() => onEdit(centre)}
            sx={{ width: 24, height: 24, p: 2 }}
          >
            <Edit />
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={centre.status || 'Standby'}
            size="small"
            sx={{
              borderRadius: 999,
              backgroundColor: style.bg,
              color: style.color,
              fontWeight: 900,
            }}
          />
        </Stack>

        {centre.assignedStaff?.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {centre.assignedStaff.map((staff) => (
              <Chip
                key={staff.user?._id || staff.user}
                size="small"
                label={`${staff.user?.firstName ?? 'Staff'} ${
                  staff.user?.lastName ?? ''
                } - ${staff.role}`}
                sx={{ borderRadius: 999, fontWeight: 800 }}
              />
            ))}
          </Stack>
        )}

        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: '#F8FAFC',
            border: '1px solid rgba(15,23,42,0.08)',
          }}
        >
          <Stack spacing={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
            >
              <Typography fontWeight={900} variant="body2">
                Patient summary
              </Typography>
              <Chip
                size="small"
                label={`${activePatientCount} active`}
                sx={{ borderRadius: 999, fontWeight: 800 }}
              />
            </Stack>

            {activePatients.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No active patients.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {activePatients.map((patient) => (
                  <Box
                    key={patient.id}
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      backgroundColor: 'white',
                      border: '1px solid rgba(15,23,42,0.08)',
                    }}
                  >
                    <Typography fontWeight={850} variant="body2" noWrap>
                      {patient.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Age {patient.age ?? 'not recorded'} -{' '}
                      {patient.gender || 'Gender not recorded'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {patient.presentingComplaint}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        </Box>

        {centre.location && (
          <Stack direction="row" spacing={1} alignItems="center">
            <LocationOn fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {centre.location}
            </Typography>
          </Stack>
        )}

        {centre.what3words && (
          <Typography variant="caption" color="text.secondary">
            ///{centre.what3words}
          </Typography>
        )}
      </Stack>
    </Paper>
  )
}

const TreatmentCentreManager = ({ controlRoomID, initialCentreID }) => {
  const [centres, setCentres] = useState([])
  const [loading, setLoading] = useState(false)
  const [openForm, setOpenForm] = useState(false)
  const [selectedCentre, setSelectedCentre] = useState(null)
  const [openedInitialCentreID, setOpenedInitialCentreID] = useState(null)

  const loadCentres = async () => {
    if (!controlRoomID) return

    setLoading(true)

    try {
      const res = await API.get(
        `/commandcontrol/treatment-centres?controlRoom=${controlRoomID}`,
      )

      if (res.status === 200) {
        setCentres(res.data)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCentres()
  }, [controlRoomID])

  useEffect(() => {
    if (!initialCentreID) {
      setOpenedInitialCentreID(null)
      return
    }

    if (
      centres.length === 0 ||
      String(openedInitialCentreID) === String(initialCentreID)
    ) {
      return
    }

    const centre = centres.find(
      (item) => String(item._id) === String(initialCentreID),
    )

    if (centre) {
      setSelectedCentre(centre)
      setOpenForm(true)
      setOpenedInitialCentreID(initialCentreID)
    }
  }, [centres, initialCentreID, openedInitialCentreID])

  const handleNew = () => {
    setSelectedCentre(null)
    setOpenForm(true)
  }

  const handleEdit = (centre) => {
    setSelectedCentre(centre)
    setOpenForm(true)
  }

  const handleSaved = (savedCentre) => {
    loadCentres()
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 4,
        border: '1px solid rgba(15,23,42,0.08)',
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1.5}
          flex={1}
        >
          <Box>
            <Typography fontWeight={900} variant="h6">
              Treatment Centres
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage medical centres, welfare areas and treatment locations.
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleNew}
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 900,
              backgroundColor: '#239AA4',
            }}
          >
            Add Centre
          </Button>
        </Stack>

        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading treatment centres…
          </Typography>
        ) : centres.length === 0 ? (
          <Box
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: '#F8FAFC',
              border: '1px dashed rgba(15,23,42,0.18)',
              textAlign: 'center',
            }}
          >
            <Typography fontWeight={800}>No treatment centres yet</Typography>
            <Typography variant="body2" color="text.secondary">
              Add your first medical centre, welfare area or treatment location.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Grid container spacing={2}>
              {centres.map((centre) => (
                <Grid item xs={12} md={6} lg={4} key={centre._id}>
                  <TreatmentCentreCard centre={centre} onEdit={handleEdit} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Stack>

      <TreatmentCentreForm
        open={openForm}
        onClose={() => setOpenForm(false)}
        controlRoomID={controlRoomID}
        centre={selectedCentre}
        onSaved={handleSaved}
      />
    </Paper>
  )
}

export default TreatmentCentreManager
